import { Component, EventEmitter, Input, OnInit, OnChanges, Output, SimpleChanges } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { TitleCasePipe } from '@angular/common';
import { IDENTIFICATION_TYPES, ISSUERS } from "src/app/constants/contact";
import { DBL_CLICK_TIMEOUT } from "src/app/constants/edit";
import { IdentificationType } from "src/app/models/identification-type.model";
import { IdentificationIssuer } from "src/app/models/identification-issuer.model";
import { Identification } from "src/app/models/identification.model";
import { MasterService } from "src/app/services/master.service";
import { MessageService } from "src/app/services/message.service";
import { StoreService } from "src/app/services/store.service";
import { Address } from "src/app/models/address.model";
import { identIcon } from "src/app/utils/icons";

@Component({
    selector: 'identifications-compact',
    templateUrl: './identifications-compact.component.html',
    styleUrls: ['./identifications-compact.component.scss'],
    providers: [TitleCasePipe],
    standalone: false
})

export class IdentificationsCompactComponent implements OnInit, OnChanges {

    /** */
    @Input() contactId: number | null = null

    /** */
    @Input() language: string = 'EN'

    /** */
    @Input() words = {}

    /** */
    @Output() modified: EventEmitter<Object> = new EventEmitter()

    /** A list of identifications associated with the current contact. */
    protected identifications: Identification[] = []

    /** A form editor for identifications. */
    protected identificationEdit: FormGroup | null = null

    /** Available communication types. */
    public identificationTypes: IdentificationType[] = IDENTIFICATION_TYPES
    
    /** Available communication subtypes. */
    public identificationIssuers: IdentificationIssuer[] = ISSUERS

    /** Company location state to pre-populate the issuer drop down. */
    protected defaultIssuer: string;

    /** A debounce timer for click events. Used to wait for the user to click twice before performing an action. */
    private dblClickWatcher: ReturnType<typeof setTimeout> = null

    constructor(private master: MasterService, private ms: MessageService, private fb: FormBuilder, private titleCasePipe: TitleCasePipe, public store: StoreService) {

        this.identificationEdit = this.fb.group({
            id: new FormControl<number>(0, [Validators.required]),
            contactId: new FormControl<number>(0, [Validators.required]),
            identificationTypeId: new FormControl<number | null>(null, [Validators.required]),
            number: new FormControl<string | null>(null),
            issuer: new FormControl<string | null>(null, [Validators.required]),
            expirationDate: new FormControl<Date>(null, [Validators.required]),
            primary: new FormControl<boolean>(false)
        })

    }

    ngOnInit(): void {
        this.determineIssuer()
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        if (changes.contactId) {
            this.fetch()
        }
    }

    /**
     * Try to determine the current company location state so we can 
     * pre-populate the issuer drop down.
     */
    private determineIssuer = (): void => {
        if (!this.store.userAccount || !this.store.userAccount['corporateAddresses'] || (!Array.isArray(this.store.userAccount['corporateAddresses'])) || (this.store.userAccount['corporateAddresses'].length <= 0)) {
            return
        }

        const address: Address = this.store.userAccount['corporateAddresses'][0]
        if (!address || !address.state) {
            return
        }

        this.defaultIssuer = address.state
    }

    /**
     * 
     */
    private fetch = (): void => {        
        if (!this.contactId || (this.contactId <= 0)) {
            return
        }
        
        this.master.get(`contacts/${this.contactId}/identifications`, (res) => {  
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            if (!res.data.identifications) {
                return
            }
            
            this.identifications = res.data.identifications
        })
    }

    /**
     * Given an identification type identity return a material icon to display.
     * @param type An identification type to map to an icon.
     */
    protected identIcon = (typeId: number): string => {
        return identIcon(typeId)
    }

    /**
     * Given a type identity return it's name based on the queried ident types.
     * @param typeId Unique internal identity of an identification type whose name will be fetched
     */
    protected typeDisplay = (typeId: number): string => {
        if (!typeId || (typeId <= 0)) {
            return 'Unknown'
        }

        if (!this.identificationTypes || (this.identificationTypes.length <= 0)) {
            return 'Unknown'
        }

        const matched = this.identificationTypes.find((type) => type.id == typeId)
        if (!matched) {
            return 'Unknown'
        }

        return matched.type
    }

    /**
     * 
     * @param e 
     */
    protected addIdentification = (e: Event): void => {
        this.cancelIdentification(e)

        if (!this.identifications) {
            this.identifications = []
        }

        const ident = new Identification()
        this.identificationEdit.patchValue({
            id: 0,
            contactId: this.contactId,
            identificationTypeId: 1, // Driver's license
            number: '',
            issuer: this.defaultIssuer,
            primary: (this.identifications.length <= 0)
        })

        this.identifications.push({
            ...ident,
            id: 0,
            primary: (this.identifications.length <= 0),
            editing: true
        })
    }

    /**
     * 
     * @param e 
     * @param index
     */
    protected editIdentification = (e: Event, index: number): void => {
        this.cancelIdentification(e)
        
        if (!this.identifications || (this.identifications.length <= 0)) {
            return
        }
        
        if (this.dblClickWatcher !== null) {
            const ident = this.identifications[index]
            this.identificationEdit.patchValue({
                ...ident
            }, {emitEvent: false})

            ident.editing = true
        }

        const ctx = this
        this.dblClickWatcher = setTimeout(() => {
            ctx.dblClickWatcher = null
        }, DBL_CLICK_TIMEOUT)
    }

    /**
     * 
     * @param e 
     */
    protected cancelIdentification = (e: Event) => {
        e.stopPropagation()
        e.preventDefault()

        this.identificationEdit.reset()
        const index = this.identifications.findIndex((item) => item.editing)
        if (index < 0) {
            return
        }

        const ident = this.identifications[index]
        if (ident.id && (ident.id > 0)) {
            ident.editing = false
            return
        }

        this.identifications.splice(index, 1)
    }

    /**
     * 
     * @param e 
     */
    protected saveIdentification = (e: Event) => {
        e.stopPropagation()
        e.preventDefault()

        const index = this.identifications.findIndex((item) => item.editing)
        if (index < 0) {
            return
        }

        const ident = Object.assign(this.identifications[index], this.identificationEdit.value)
        const type = this.identificationTypes.find((identType) => identType.id == ident.identificationTypeId)
        ident.identificationType = type

        if (ident.id && (ident.id > 0)) {
            this.modifyIdentification(ident)
            return
        }

        this.insertIdentification(ident)
    }

    /**
     * 
     * @param ident 
     */
    private insertIdentification = (ident: Identification) => {
        if (!ident) {
            return
        }

        const payload = {
            identifications: [
                JSON.parse(JSON.stringify(ident))
            ]
        }

        delete(payload.identifications[0].id)
        delete(payload.identifications[0].type)

        this.master.post(`contacts/${this.contactId}/identifications`, payload, (res) => {  
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            ident.editing = false
            this.identificationEdit.reset()

            if (!res.data || !res.data.identifications || (res.data.identifications.length <= 0)) {
                return
            }
            
            ident.id = parseInt(res.data.identifications[0].id)
            this.modified.emit({identifications: [{id: ident.id}]})
        })
    }

    /**
     * 
     * @param ident 
     */
    private modifyIdentification = (ident: Identification) => {
        if (!ident || !ident.id) {
            return
        }

        const payload = JSON.parse(JSON.stringify(ident))
        
        delete(payload.id)
        delete(payload.type)

        this.master.put(`contacts/${this.contactId}/identifications/${ident.id}`, {identification: payload}, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }
            
            ident.editing = false
            this.identificationEdit.reset()
            this.modified.emit({identifications: [{id: ident.id}]})
        })
    }

    /**
     * 
     * @param e 
     * @param index
     */
    protected discardIdentification = (e: Event, index: number): void => {
        if (!this.identifications || (this.identifications.length <= 0) || (index < 0) || (index >= this.identifications.length)) {
            return
        }

        const ident = this.identifications[index]
        const identId = ident.id

        this.master.discard(`contacts/${this.contactId}/identifications/${identId}`, (res) => {  
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }
            
            this.identifications.splice(index, 1)
            this.modified.emit({identifications: [{id: ident.id}]})
        })
    }
}